/**
 * Fixed booking, inline: pick a day on the calendar, a time beside it, and
 * optionally the creative who does it.
 *
 * The prototype puts these three columns in a dialog. Here they are three
 * columns of a panel on the page, so the cart, the address and the price the
 * client is scheduling against stay in view while they choose — and the columns
 * stack on a narrow viewport instead of a dialog growing a scrollbar.
 *
 * The creative column appears only when the client asks for a specific person.
 * Off is the better default and the toggle says why: Backbone assigns whoever
 * fits and no travel fee applies.
 *
 * The calendar offers every day between the first bookable slot and the latest
 * bookable day. The time stays locked until a day is picked, and on the first
 * day only the slots from the lead-time floor on can be picked.
 */

import React, { useMemo } from 'react'
import { Stack, Typography } from '@mui/material'
import { DateCalendar } from '@mui/x-date-pickers'
import moment, { Moment } from 'moment-timezone'
import { useTranslation } from 'react-i18next'

import { BEIGE_700, BLUE_200, BLUE_300, CORAL_600, GRAY_1000, GRAY_500, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import type { RevampCreative } from 'dataQueries/purchaseFlowRevampCreatives.query'

import { ChooseCreativeToggle } from './ChooseCreativeToggle.component'
import { ScheduleCreativeList } from './ScheduleCreativeList.component'
import { ShootingCalendarHeader } from './ShootingCalendarHeader.component'
import { ShootingTimePicker } from './ShootingTimePicker.component'
import { ShootingTimezoneNotice } from './ShootingTimezoneNotice.component'
import { MUIDivider } from 'components/common/MUIDivider'
import { SystemMessage } from 'components/common/SystemMessage'

import { earliestSlot, isBeforeEarliestSlot } from './shootingDateTime'
import { formatMaxDateTime, formatMinDateTime, isMeetingDateTooFar, resolveMeetingMaxDateTime } from '../../leadTime.helpers'

interface Props {
  /** The chosen day, or undefined while none is chosen. */
  day?: Moment | null
  onPickDay: (day: Moment | null) => void
  hour?: number
  minute?: number
  onPickHour: (hour: number) => void
  onPickMinute: (minute: number | undefined) => void
  /** Whether the client is choosing the creative themselves. */
  canChooseCreative: boolean
  /** The last list came back empty, so the toggle switched itself off. */
  noCreativesAvailable?: boolean
  choosingCreative: boolean
  onToggleChoosingCreative: (next: boolean) => void
  creatives: ReadonlyArray<RevampCreative>
  creativeId?: string
  onPickCreative: (id: string) => void
  isCreativesLoading?: boolean
  /** Earliest bookable moment — the order's lead time. */
  minDateTime?: Moment
  /** The shoot's timezone, and the client's, for the mismatch notice. */
  timezone?: string
  userTimezone?: string
  /** The completed pick, ISO, for the dual-clock echo. */
  scheduledAt?: string
}

export const FixedBookingPanel: React.FC<Props> = ({
  day,
  onPickDay,
  hour,
  minute,
  onPickHour,
  onPickMinute,
  canChooseCreative,
  noCreativesAvailable = false,
  choosingCreative,
  onToggleChoosingCreative,
  creatives,
  creativeId,
  onPickCreative,
  isCreativesLoading = false,
  minDateTime,
  timezone,
  userTimezone,
  scheduledAt,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp', 'purchase_flow'])

  // The calendar stops at the earliest day but the time picker has no floor, so the earliest day can
  // still get too early an hour, which the order cannot proceed with.
  const tooSoon = !!scheduledAt && !!minDateTime && !!timezone && moment(scheduledAt).isBefore(minDateTime)

  const earliest = useMemo(() => earliestSlot(minDateTime, timezone), [minDateTime, timezone])
  const isMinuteBeforeEarliest = (forHour: number | undefined, value: number) =>
    forHour !== undefined && isBeforeEarliestSlot(earliest, day, forHour, value)
  const pickHour = (value: number) => {
    onPickHour(value)
    if (minute !== undefined && isMinuteBeforeEarliest(value, minute)) onPickMinute(undefined)
  }

  const maxDateTime = useMemo(() => resolveMeetingMaxDateTime(timezone), [timezone])
  const tooFar = !!scheduledAt && isMeetingDateTooFar(scheduledAt, maxDateTime, timezone)

  return (
    <Stack gap={1.6} data-cy="pf-revamp-fixed-booking-panel" data-pf-gap="meeting_date_invalid meeting_minutes_missing">

      {canChooseCreative &&
      <Stack gap={0.8}>

        <ChooseCreativeToggle
          checked={choosingCreative}
          onToggle={onToggleChoosingCreative}
          noFavourites={noCreativesAvailable}
        />

        {/* While the list loads the spinner sits under the switch: opening the list column first and closing it
            again when no creative comes back made the panel jump. */}
        {choosingCreative && isCreativesLoading &&
          <ScheduleCreativeList creatives={[]} onSelect={onPickCreative} isLoading />
        }

      </Stack>
      }

      <ShootingTimezoneNotice timezone={timezone} userTimezone={userTimezone} scheduledAt={scheduledAt} />

      {/* Column on a wide viewport, stacked below it. The calendar sets the
          middle column's width, so the other two share what is left. */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap={1.6}
        divider={<MUIDivider orientation="vertical" flexItem margin={0} color={BEIGE_700} sx={{ display: { xs: 'none', md: 'block' } }} />}
        alignItems="flex-start"
      >

        {canChooseCreative && choosingCreative && !isCreativesLoading && creatives.length > 0 &&
          <Stack gap={0.8} sx={{ flex: 1, minWidth: 0, width: '100%' }}>

            <Typography variant="text-sm" fontWeight={550} color={GRAY_900}>
              {t('purchase_flow_revamp:instructions.schedule.choose_creative_toggle')}
            </Typography>

            <ScheduleCreativeList
              creatives={creatives}
              value={creativeId}
              onSelect={onPickCreative}
            />

          </Stack>
        }

        {/* 38.4rem is the calendar's design width and it must not shrink beside
            the other two columns — but a fixed width plus the panel's own
            padding overflows a 390px phone, so it goes fluid below `md`. */}
        <Stack sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' }, minWidth: 0 }}>
          <DateCalendar
            value={day ?? null}
            onChange={(picked) => onPickDay(picked as Moment | null)}
            disablePast
            // One clock for the grid and its bounds: a client whose own timezone differs from the shoot's got
            // the month built in one and the earliest day in the other, which left a few disabled days.
            timezone={timezone || 'default'}
            {...(earliest ? { minDate: earliest.day } : {})}
            maxDate={timezone ? moment(maxDateTime).tz(timezone) : moment(maxDateTime)}
            showDaysOutsideCurrentMonth
            fixedWeekNumber={6}
            dayOfWeekFormatter={(_day, date) => (date as Moment).format('ddd')}
            slots={{ calendarHeader: ShootingCalendarHeader }}
            sx={{
              width: { xs: '100%', md: '38.4rem' },
              maxWidth: '38.4rem',
              height: 'auto',
              maxHeight: 'none',
              m: 0,
              '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': { justifyContent: 'space-between', gap: 0.8, m: 0 },
              '& .MuiDayCalendar-weekContainer:not(:first-of-type)': { mt: 0.8 },
              '& .MuiDayCalendar-header': { mb: 0.8 },
              // The month slides in absolutely positioned, so the grid's height is reserved here: six weeks of
              // square cells (seven per row, 0.8rem apart, at most 4.8rem) plus the five gaps between them.
              containerType: 'inline-size',
              '& .MuiDayCalendar-slideTransition': { minHeight: 'calc(6 * min(4.8rem, (100cqw - 4.8rem) / 7) + 4rem)' },
              '& .MuiDayCalendar-weekDayLabel': { flex: 1, width: 'auto', height: '1.8rem', m: 0, fontSize: '1.2rem', lineHeight: '1.8rem', fontWeight: 450, color: GRAY_700 },
              '& .MuiPickersDay-root, & .MuiPickersDay-hiddenDaySpacingFiller': { flex: 1, width: 'auto', height: 'auto', aspectRatio: '1', maxWidth: '4.8rem', m: 0 },
              '& .MuiPickersDay-root': {
                position: 'relative',
                borderRadius: '0.8rem',
                fontSize: '1.6rem',
                fontWeight: 450,
                color: GRAY_900,
                backgroundColor: BLUE_200,
                border: 'none',
                '&.MuiPickersDay-today:not(.Mui-selected)': { border: 'none' },
                '&:hover': { backgroundColor: BLUE_300 },
                '&.Mui-disabled:not(.Mui-selected), &.MuiPickersDay-dayOutsideMonth': { color: GRAY_500, backgroundColor: 'transparent' },
                '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': { color: WHITE, backgroundColor: GRAY_1000 },
              },
              '& .MuiPickersDay-today::after': {
                content: '""',
                position: 'absolute',
                top: 'calc(50% + 0.9rem)',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '0.4rem',
                height: '0.4rem',
                borderRadius: '50%',
                backgroundColor: CORAL_600,
              },
            }}
          />
        </Stack>

        <Stack gap={0.8} sx={{ flex: 1, minWidth: 0, width: '100%', alignSelf: { md: 'stretch' } }}>

          <ShootingTimePicker
            hour={hour}
            minute={minute}
            onPickHour={pickHour}
            onPickMinute={onPickMinute}
            disabled={!day}
            isHourDisabled={(value) => isBeforeEarliestSlot(earliest, day, value)}
            isMinuteDisabled={(value) => isMinuteBeforeEarliest(hour, value)}
          />

          {tooSoon &&
            <SystemMessage
              variant="error"
              message={t('purchase_flow:validation_errors.date_too_soon', {
                dateString: formatMinDateTime(minDateTime.toDate(), timezone),
              })}
            />
          }

          {tooFar &&
            <SystemMessage
              variant="error"
              message={t('purchase_flow:validation_errors.date_too_far', {
                dateString: formatMaxDateTime(maxDateTime, timezone),
              })}
            />
          }

        </Stack>

      </Stack>

    </Stack>
  )
}
